import {
  CheckOutlined,
  CopyOutlined,
  LinkOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import type { Options as Html2CanvasOptions } from 'html2canvas-pro'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { OPERATION_STATUS, runOperation } from 'clients/exception-handling'
import { TooltipIconButton } from 'components/TooltipIconButton'
import { useHyperCopy } from 'features/project/hooks/use-hyper-copy'
import { useProjectStore } from 'features/project/store'
import mixpanel from 'trackers/mixpanel'
import { ProjectState } from 'types/project'

export interface UseCopyToolbarArgs {
  elementName: string
  copyUrl?: string
  message_uuid?: string
  conversation_uuid?: string
  html2canvasOptions?: Partial<Html2CanvasOptions>
  /** TooltipIconButton variant for the toolbar's own icons. Defaults to
   *  'muted' (grey) to preserve existing behavior for callers that render
   *  `toolbar`. */
  variant?: 'default' | 'muted'
}

export interface UseCopyToolbarResult {
  /** Attach to whatever element should be captured by "copy as image" and
   *  selected when the global hyper-copy mode is on. */
  ref: React.RefObject<HTMLDivElement>
  /** Whether the project-wide hyper-copy (click-to-select) mode is on. */
  isHyperCopy: boolean
  /** Marks `ref.current` as the copy-as-image target. */
  selectForCopy: () => void
  /** The two icon buttons (copy-as-link, copy-as-image) with no wrapping
   *  element — the caller owns layout/positioning. */
  toolbar: React.ReactNode
}

// Extracted from HyperDiv so a consumer that needs the copy-toolbar's logic
// without HyperDiv's absolutely-positioned overlay (e.g. a header row that
// wants the icons as an ordinary flex child) can use it directly.
export function useCopyToolbar({
  elementName,
  copyUrl,
  message_uuid,
  conversation_uuid,
  html2canvasOptions,
  variant = 'muted',
}: UseCopyToolbarArgs): UseCopyToolbarResult {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const isHyperCopy = useProjectStore(
    (state: ProjectState) => state.isHyperCopy,
  )
  const [selectedElement, setSelectedElement] = useState<HTMLDivElement | null>(
    null,
  )
  const ref = useRef<HTMLDivElement>(null)

  const { isCopying, lastCopied } = useHyperCopy(
    selectedElement,
    setSelectedElement,
    html2canvasOptions,
  )

  const [copyHovered, setCopyHovered] = useState(false)
  const [showCheck, setShowCheck] = useState(false)

  // Link copy state
  const [showLinkCheck, setShowLinkCheck] = useState(false)

  useEffect(() => {
    if (lastCopied === ref.current) {
      setShowCheck(true)
    }
  }, [lastCopied])

  useEffect(() => {
    if (showCheck && !copyHovered) {
      setShowCheck(false)
      setSelectedElement(null)
    }
  }, [showCheck, copyHovered])

  const copyUrlToClipboard = async (): Promise<void> => {
    if (copyUrl) {
      const result = await runOperation(
        () => navigator.clipboard.writeText(copyUrl),
        {
          operation: 'clipboard.copy-link',
          isExpectedError: (cause) =>
            cause instanceof DOMException && cause.name === 'NotAllowedError',
        },
      )
      if (result.status === OPERATION_STATUS.SUCCEEDED) {
        setShowLinkCheck(true)
        setTimeout(() => setShowLinkCheck(false), 2000)
      } else if (
        result.status === OPERATION_STATUS.FAILED ||
        result.status === OPERATION_STATUS.EXPECTED
      ) {
        toast.error('Unable to copy this link. Please try again.')
      }
    }
  }

  const selectForCopy = (): void => setSelectedElement(ref.current)

  const toolbar = (
    <>
      {copyUrl && (
        <TooltipIconButton
          tooltipTitle="Copy as link"
          ariaLabel="Copy as link"
          variant={variant}
          height={28}
          width={28}
          iconFontSize="13px"
          icon={showLinkCheck ? <CheckOutlined /> : <LinkOutlined />}
          onClick={() => {
            copyUrlToClipboard()
            mixpanel.track('copy_verbatim_link', {
              proj_uuid: projectId,
              value: elementName,
              message_uuid,
              conversation_uuid,
            })
          }}
        />
      )}

      <TooltipIconButton
        tooltipTitle="Copy as image"
        ariaLabel="Copy as image"
        variant={variant}
        height={28}
        width={28}
        iconFontSize="13px"
        icon={
          isCopying ? (
            <LoadingOutlined />
          ) : showCheck ? (
            <CheckOutlined />
          ) : (
            <CopyOutlined />
          )
        }
        onClick={() => {
          selectForCopy()
          mixpanel.track('copy', {
            proj_uuid: projectId,
            value: elementName,
          })
        }}
        onMouseEnter={() => setCopyHovered(true)}
        onMouseLeave={() => setCopyHovered(false)}
      />
    </>
  )

  return { ref, isHyperCopy, selectForCopy, toolbar }
}
